/**
 * Minimal rehype plugin that strips scripting from a hast tree
 * (openspec: security-controls-round-4 B5).
 *
 * The blog renders markdown with react-markdown + rehype-raw, so raw HTML in
 * a post becomes real elements. React refuses to attach string `on*` handlers
 * and does not execute client-created <script> elements, but the page is
 * server-rendered: a `<script>` in post markdown WOULD run during the
 * initial HTML parse. This plugin runs after rehype-raw and removes:
 *   - scripting / embedding / form elements outright
 *   - `on*` attributes
 *   - `javascript:` / `vbscript:` / non-image `data:` URLs
 *   - unsafe CSS in `style` (see lib/sanitize-css.ts)
 *
 * Dependency-free (plain tree walk) so it stays out of the DOMPurify bundle.
 */
import { scrubCss } from './sanitize-css'

interface HastNode {
  type: string
  tagName?: string
  properties?: Record<string, unknown>
  children?: HastNode[]
  value?: string
}

const DROP_TAGS = new Set([
  'script', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'base', 'meta', 'link',
  'style', 'form', 'input', 'button', 'textarea', 'select', 'option', 'keygen', 'foreignObject', 'math',
])

// hast property names (property-information camelCase) that carry URLs.
const URL_PROPS = new Set([
  'href', 'src', 'srcSet', 'xLinkHref', 'xlinkHref', 'action', 'formAction', 'poster', 'data', 'cite',
  'background', 'ping', 'codeBase', 'longDesc',
])

function isSafeUrl(value: unknown, prop: string): boolean {
  if (typeof value !== 'string') return true
  // Browsers ignore control characters and whitespace inside a scheme
  // ("java\tscript:"), so strip them before comparing.
  const v = Array.from(value)
    .filter((ch) => ch.charCodeAt(0) > 0x20 && !/\s/.test(ch))
    .join('')
    .toLowerCase()
  if (v.startsWith('javascript:') || v.startsWith('vbscript:') || v.startsWith('livescript:')) return false
  if (v.startsWith('data:')) return prop === 'src' && v.startsWith('data:image/')
  return true
}

function scrubProperties(props: Record<string, unknown>): void {
  for (const key of Object.keys(props)) {
    if (/^on[a-z]/i.test(key)) {
      delete props[key]
      continue
    }
    if (URL_PROPS.has(key)) {
      const value = props[key]
      const values = Array.isArray(value) ? value : [value]
      if (!values.every((v) => isSafeUrl(v, key))) delete props[key]
      continue
    }
    if (key === 'style' && typeof props[key] === 'string') {
      props[key] = scrubCss(props[key] as string)
    }
  }
}

function walk(node: HastNode): void {
  if (!node.children) return
  node.children = node.children.filter((child) => {
    if (child.type === 'element' && child.tagName && DROP_TAGS.has(child.tagName)) return false
    return true
  })
  for (const child of node.children) {
    if (child.type === 'element' && child.properties) scrubProperties(child.properties)
    walk(child)
  }
}

/** rehype plugin: `rehypePlugins={[rehypeRaw, rehypeStripUnsafe]}` */
export function rehypeStripUnsafe() {
  return (tree: HastNode) => {
    walk(tree)
  }
}
